import { UserIcon } from './icons';
import { avatarFallback } from './avatarFallback';
import './avatar.css';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  src?: string | null;
  /** Used for the initial fallback; never rendered as image alt text. */
  name?: string;
  /** Fallback letter source when the person has no name. */
  username?: string | null;
  /**
   * Stable identity (profileUserGuid, userId, ...) that fixes the fallback
   * color. Falls back to the name when a surface has no id to hand.
   */
  seed?: string | number | null;
  size?: AvatarSize;
  /** Rounded-square instead of a circle (profile header in the Figma design). */
  square?: boolean;
  className?: string;
}

const ICON_SIZE: Record<AvatarSize, number> = { sm: 16, md: 20, lg: 24, xl: 36 };

export function Avatar({
  src,
  name,
  username,
  seed,
  size = 'md',
  square = false,
  className = '',
}: AvatarProps) {
  const classes = [
    'ui-avatar',
    `ui-avatar--${size}`,
    square ? 'ui-avatar--square' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const { initial, tone } = avatarFallback({ seed, name, username });

  return (
    <span className={classes} data-avatar-tone={src || !initial ? undefined : tone}>
      {/* The surrounding control already names the person, so the image is decorative. */}
      {src ? <img src={src} alt="" aria-hidden /> : initial || <UserIcon size={ICON_SIZE[size]} />}
    </span>
  );
}
